import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PositionService } from '../services/positionService';
import { Position } from '../types';
import { formatCurrency } from '../utils/formatters';

const FixPositionDates: React.FC = () => {
  const [positionId, setPositionId] = useState('');
  const [newEntryDate, setNewEntryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const handleFetchPosition = async () => {
    if (!positionId.trim()) {
      setError('Please enter a position ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    setPosition(null);

    try {
      const fetchedPosition = await PositionService.getPosition(positionId.trim());
      if (fetchedPosition) {
        setPosition(fetchedPosition);
        // Set the current entry date as the default for the input
        const currentDate = formatInTimeZone(fetchedPosition.entryDate, 'America/New_York', "yyyy-MM-dd'T'HH:mm");
        setNewEntryDate(currentDate);
      } else {
        setError('Position not found');
      }
    } catch (err) {
      setError('Failed to fetch position: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePosition = async () => {
    if (!position || !newEntryDate) {
      setError('Please fetch a position and set a new entry date');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Parse the input date (which is in EST) and convert to Date object
      const newDate = parseISO(newEntryDate);
      
      // Update the position in Firestore
      const positionRef = doc(db, 'positions', position.id);
      await updateDoc(positionRef, {
        entryDate: newDate
      });

      // Fetch the updated position to show the new values
      const updatedPosition = await PositionService.getPosition(position.id);
      if (updatedPosition) {
        setPosition(updatedPosition);
      }

      setSuccessMessage('Position entry date updated successfully!');
    } catch (err) {
      setError('Failed to update position: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateInEST = (date: Date) => {
    return formatInTimeZone(date, 'America/New_York', 'PPP p');
  };

  const calculateDaysHeld = (entryDate: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - entryDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Fix Position Entry Dates
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This utility allows you to update the entry date for a specific position. 
        Use this carefully as it affects holding days calculations.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Position ID"
              value={positionId}
              onChange={(e) => setPositionId(e.target.value)}
              placeholder="e.g., ItsQ2F8km5TlTHU99BbN"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleFetchPosition}
              disabled={loading || !positionId.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Fetch Position'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {position && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Position Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Symbol</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {position.symbol}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {position.quantity} shares
                </Typography>

                <Typography variant="body2" color="text.secondary">Entry Price</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {formatCurrency(position.entryPrice)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Current Entry Date (EST)</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {formatDateInEST(position.entryDate)}
                </Typography>

                <Typography variant="body2" color="text.secondary">Current Holding Days</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {position.holdingDays} days
                </Typography>

                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {position.status}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Update Entry Date
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="New Entry Date (EST)"
                  value={newEntryDate}
                  onChange={(e) => setNewEntryDate(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {newEntryDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    New holding days will be: {calculateDaysHeld(parseISO(newEntryDate))} days
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleUpdatePosition}
                  disabled={loading || !newEntryDate}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Position'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Alert severity="warning">
        <Typography variant="body2">
          <strong>Warning:</strong> This tool directly updates the position entry date in Firestore. 
          This will affect holding days calculations and portfolio metrics. Use with caution and 
          verify the changes are correct before proceeding.
        </Typography>
      </Alert>
    </Box>
  );
};

export default FixPositionDates;